import React, { useCallback, useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'

import { Box } from '@factorialco/deprecated-design-system'

import FromField from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Trip/FromField'
import ToField from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Trip/ToField'
import { CustomizationProps } from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import { PermissionsProps } from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'
import { convertToNumber } from 'modules/expenses/lib/numberFormating'
import { calculateDistance } from 'modules/locations/hooks/useAutocompleteAddress'

interface Props extends PermissionsProps, CustomizationProps {
  setIsCalculatingDistance?: (loading: boolean) => void
}

interface Place {
  lat: number
  lng: number
}

const Trip = (props: Props) => {
  const { setValue, watch } = useFormContext()
  const { setIsCalculatingDistance } = props

  // Track if we're initializing with existing data
  const isInitializedRef = useRef(false)
  const hasExistingData = Boolean(props.expensable?.id)

  // Watch form values
  const originLatitude = watch('originLatitude')
  const originLongitude = watch('originLongitude')
  const destinationLatitude = watch('destinationLatitude')
  const destinationLongitude = watch('destinationLongitude')
  const units = watch('units')

  const roundTrip = watch('roundTrip')

  const recalculateDistance = useCallback(async () => {
    if (!destinationLatitude || !destinationLongitude || !originLatitude || !originLongitude) {
      return
    }

    if (!setIsCalculatingDistance) {
      return
    }

    setIsCalculatingDistance(true)

    try {
      // We are ignoring the roudntrip during the request
      // We'll apply it after the request done so that calculatedMileage represents
      // the distance from origin and destination without roundtrip, as this causes
      // inconsistencies for the mileage alert.
      const distance = await calculateDistance({
        latOrigin: originLatitude,
        lonOrigin: originLongitude,
        latDestination: destinationLatitude,
        lonDestination: destinationLongitude,
        roundTrip: false,
        units,
      })

      if (distance) {
        const distanceValue = convertToNumber(distance)

        // Handles if roundtrip is enabled when the user checks
        if (roundTrip) {
          setValue('mileage', distanceValue * 2, { shouldValidate: true, shouldDirty: true })
        } else {
          setValue('mileage', distanceValue, { shouldValidate: true, shouldDirty: true })
        }
        // Keeps the calculated distance to just match the distance from origin and destination without roundtrip.
        setValue('calculatedMileage', distanceValue, { shouldValidate: true, shouldDirty: true })
      }
    } finally {
      setIsCalculatingDistance(false)
    }
  }, [
    originLatitude,
    originLongitude,
    destinationLatitude,
    destinationLongitude,
    units,
    setValue,
    setIsCalculatingDistance,
  ])

  // Watch for changes in coordinates and recalculate distance when both sets are available
  useEffect(() => {
    // Skip initial calculation when editing existing data

    if (hasExistingData && !isInitializedRef.current) {
      isInitializedRef.current = true
      return
    }

    if (originLatitude && destinationLatitude) {
      recalculateDistance()
    }
  }, [originLatitude, destinationLatitude, units, hasExistingData])

  const handleFromPlaceSelected = useCallback(
    (place: Place) => {
      setValue('originLatitude', String(place.lat), { shouldValidate: true, shouldDirty: true })
      setValue('originLongitude', String(place.lng), {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [setValue]
  )

  const handleToPlaceSelected = useCallback(
    (place: Place) => {
      setValue('destinationLatitude', String(place.lat), {
        shouldValidate: true,
        shouldDirty: true,
      })
      setValue('destinationLongitude', String(place.lng), {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [setValue]
  )

  return (
    <Box gap="s16">
      <FromField {...props} fieldIdentifier="from" onPlaceSelected={handleFromPlaceSelected} />
      <ToField {...props} fieldIdentifier="to" onPlaceSelected={handleToPlaceSelected} />
    </Box>
  )
}

export default Trip
